import { Trash2 } from "lucide-react";
import React, { useMemo } from "react";

import {
  IgAlert,
  IgIconButton,
  IgLink,
  IgPaper,
  IgSelect,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { UrlMatch, UrlMatchList } from "@intelligems/ig-types";
import { MenuItem, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { getTargetPreviewDisplayValue } from "@src/components/targeting/utils/utils";
import { customEventsPageScopingLink } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

const hasFullUrlProtocol = (value: string): boolean => {
  return /^https?:\/\//i.test(value.trim());
};

const extractPathFromUrl = (value: string): string | null => {
  try {
    const url = new URL(value.trim());
    const path = url.pathname.replace(/^\//, "");
    return path || null;
  } catch {
    return null;
  }
};

interface CustomEventUrlProps {
  url: { matchBy: UrlMatch; value: string };
  index: number;
  isArchived: boolean;
  onMatchChange: (index: number, newValue: UrlMatch) => void;
  onUrlChange: (index: number, newValue: string) => void;
  onRemove: (index: number) => void;
  domain: string;
  error?: string | null;
}

const TargetPreview = ({
  previewDisplayValue,
  url,
}: {
  previewDisplayValue: string;
  url: { matchBy: UrlMatch; value: string };
}) => {
  const isHomepage =
    !url.value.trim() &&
    (url.matchBy === "equals" || url.matchBy === "doesNotEqual");

  return (
    <Grid marginTop={"10px"}>
      <IgTypography fontWeight={500}>Target Preview: </IgTypography>
      <IgTypography>
        {previewDisplayValue} {isHomepage ? "(homepage)" : null}
      </IgTypography>
    </Grid>
  );
};

export const CustomEventUrl = ({
  url,
  index,
  isArchived,
  onMatchChange,
  onUrlChange,
  onRemove,
  domain,
  error,
}: CustomEventUrlProps) => {
  const theme = useTheme();
  const urlLabels: Record<UrlMatch, string> = {
    contains: "contains",
    equals: "equals",
    startsWith: "starts with",
    endsWith: "ends with",
    doesNotEqual: "does not equal",
    doesNotContain: "does not contain",
    doesNotStartWith: "does not start with",
    doesNotEndWith: "does not end with",
    isNull: "is null",
    isNotNull: "is not null",
    regex: "matches regex",
  };

  const previewDisplayValue = getTargetPreviewDisplayValue(
    url.matchBy,
    domain,
    url.value
  );

  const isFullUrl = hasFullUrlProtocol(url.value);
  const suggestedPath = useMemo(
    () => (isFullUrl ? extractPathFromUrl(url.value) : null),
    [isFullUrl, url.value]
  );
  const shouldShowLeadingSlashWarning =
    url.value.startsWith("/") && url.matchBy === "contains";
  const shouldShowFullUrlInfo =
    isFullUrl && url.matchBy !== "regex" && url.matchBy !== "equals";

  return (
    <IgPaper
      sx={{
        backgroundColor: theme.igPrimitives.gray[200],
        borderRadius: "5px",
        boxShadow: "none",
        padding: "20px",
        marginBottom: "15px",
      }}
    >
      <Grid container direction={"column"}>
        <Grid
          container
          alignItems={"center"}
          justifyContent={"space-between"}
          key={index}
        >
          <Grid>
            <IgSelect
              value={url.matchBy}
              onChange={(e) => onMatchChange(index, e.target.value)}
              variant={"outlined"}
              disabled={isArchived}
              MenuProps={{ disablePortal: true }}
              sx={{
                width: "205px",
                "& .Mui-disabled": {
                  backgroundColor: theme.igPrimitives.gray[300],
                },
              }}
            >
              {UrlMatchList.filter(
                (match) => match !== "isNull" && match !== "isNotNull"
              ).map((match) => (
                <MenuItem key={`filter-option-${match}`} value={match}>
                  {`URL ${urlLabels[match]}`}
                </MenuItem>
              ))}
            </IgSelect>
          </Grid>
          {!isArchived && (
            <Grid>
              <IgIconButton onClick={() => onRemove(index)}>
                <Trash2
                  size={18}
                  className="trash-can"
                  color={theme.igIconColors.error}
                />
              </IgIconButton>
            </Grid>
          )}
        </Grid>
        {shouldShowLeadingSlashWarning && (
          <Grid marginTop={"15px"}>
            <IgAlert
              alertKey={`leading-slash-warning-${index}`}
              severity="warning"
              message={
                <>
                  URL matching happens on the page path after the initial slash,
                  so you should generally not have a leading slash.{" "}
                  <IgLink
                    onClick={() => openNewTab(customEventsPageScopingLink)}
                  >
                    Learn more
                  </IgLink>
                  .
                </>
              }
            />
          </Grid>
        )}
        {shouldShowFullUrlInfo && (
          <Grid marginTop={"15px"}>
            <IgAlert
              alertKey={`full-url-info-${index}`}
              severity="info"
              message={
                <>
                  You can just enter the path directly
                  {suggestedPath ? ` (e.g. ${suggestedPath})` : ""}.{" "}
                  <IgLink
                    onClick={() => openNewTab(customEventsPageScopingLink)}
                  >
                    Learn more
                  </IgLink>
                </>
              }
            />
          </Grid>
        )}
        <Grid container marginTop={"15px"} direction={"column"}>
          <Grid>
            <IgTextField
              placeholder={
                url.matchBy === "regex"
                  ? ".*collections/products"
                  : "pages/mypages"
              }
              disabled={isArchived}
              sx={{
                backgroundColor: "white",
                width: "100%",
                "& .Mui-disabled": {
                  backgroundColor: theme.igPrimitives.gray[300],
                },
              }}
              value={url.value}
              onChange={(e) => onUrlChange(index, e.target.value)}
            />
          </Grid>
          {url.matchBy === "regex" && error ? (
            <Grid marginTop={"10px"}>
              <IgTypography sx={{ color: theme.palette.text.tonal.error }}>
                {error}
              </IgTypography>
            </Grid>
          ) : (
            <TargetPreview
              previewDisplayValue={previewDisplayValue}
              url={url}
            />
          )}
        </Grid>
      </Grid>
    </IgPaper>
  );
};
